import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { adminStoryService, QueueStats } from '../../services/adminStoryService';
import { dashboardService } from '../../services/dashboardService';
import { LoadingSpinner } from '../../shared/components';
import toast from 'react-hot-toast';

const ReportsContainer = styled.div`
  padding: 2rem;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-gray-800);
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  background: var(--color-white);
  font-size: 0.875rem;
  min-width: 120px;
`;

const DateInput = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  background: var(--color-white);
  font-size: 0.875rem;
`;

const ReportsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const ReportCard = styled.div`
  background: var(--color-white);
  padding: 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  
  h3 {
    margin: 0 0 1rem 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--color-gray-800);
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const MetricCard = styled.div`
  background: var(--color-white);
  padding: 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  text-align: center;
`;

const MetricValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MetricLabel = styled.div`
  font-size: 0.875rem;
  color: var(--color-gray-600);
  font-weight: 500;
`;

const TrendIndicator = styled.span<{ trend: 'up' | 'down' | 'neutral' }>`
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-weight: 600;
  
  ${props => {
    switch (props.trend) {
      case 'up':
        return `
          background: #D1FAE5;
          color: #059669;
        `;
      case 'down':
        return `
          background: #FEE2E2;
          color: #DC2626;
        `;
      case 'neutral':
      default:
        return `
          background: #F3F4F6;
          color: #6B7280;
        `;
    }
  }}
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-gray-800);
  margin: 3rem 0 1.5rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--color-gray-200);
`;

const ExportButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--color-primary);
  color: var(--color-white);
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: var(--color-primary-dark);
  }
`;

const AlertBadge = styled.div<{ severity: 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  
  ${props => {
    switch (props.severity) {
      case 'HIGH':
        return `
          background: #FEE2E2;
          color: #DC2626;
          border-left: 4px solid #DC2626;
        `;
      case 'MEDIUM':
        return `
          background: #FEF3C7;
          color: #D97706;
          border-left: 4px solid #D97706;
        `;
      case 'LOW':
        return `
          background: #DBEAFE;
          color: #2563EB;
          border-left: 4px solid #2563EB;
        `;
      case 'INFO':
      default:
        return `
          background: #F0F9FF;
          color: #0369A1;
          border-left: 4px solid #0369A1;
        `;
    }
  }}
`;

const LiveMetricCard = styled.div`
  background: var(--color-white);
  padding: 1rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-gray-200);
  
  .metric-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }
  
  .metric-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--color-gray-600);
  }
  
  .metric-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--color-primary);
  }
  
  .metric-unit {
    font-size: 0.75rem;
    color: var(--color-gray-500);
    margin-left: 0.25rem;
  }
`;

const RefreshIndicator = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--color-gray-500);
  
  .refresh-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.active ? '#10B981' : '#6B7280'};
    animation: ${props => props.active ? 'pulse 2s infinite' : 'none'};
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const AutoRefreshToggle = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${props => props.active ? 'var(--color-primary)' : 'var(--color-gray-200)'};
  color: ${props => props.active ? 'var(--color-white)' : 'var(--color-gray-600)'};
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? 'var(--color-primary-dark)' : 'var(--color-gray-300)'};
  }
`;

const ForecastChart = styled.div`
  .forecast-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem;
    margin-bottom: 0.5rem;
    background: #F9FAFB;
    border-radius: var(--border-radius-md);
    border-left: 3px solid var(--color-primary);
    
    .forecast-period {
      font-weight: 500;
      color: var(--color-gray-700);
    }
    
    .forecast-value {
      font-weight: 700;
      color: var(--color-primary);
    }
    
    .confidence-badge {
      padding: 0.25rem 0.5rem;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 600;
      background: #E0F2FE;
      color: #0369A1;
    }
  }
`;

const ReportBuilder = styled.div`
  background: var(--color-white);
  padding: 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  
  .config-section {
    margin-bottom: 1.5rem;
    
    label {
      display: block;
      font-weight: 500;
      margin-bottom: 0.5rem;
      color: var(--color-gray-700);
    }
    
    select, input {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid var(--color-gray-300);
      border-radius: var(--border-radius-md);
      background: var(--color-white);
      font-size: 0.875rem;
    }
  }
  
  .metrics-checkboxes {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    
    label {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: normal;
      cursor: pointer;
      
      input[type="checkbox"] {
        width: auto;
      }
    }
  }
`;

const GenerateButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--color-primary);
  color: var(--color-white);
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  width: 100%;
  
  &:hover {
    background: var(--color-primary-dark);
  }
  
  &:disabled {
    background: var(--color-gray-400);
    cursor: not-allowed;
  }
`;

const TrendBadge = styled.span<{ trend: 'ACCELERATING' | 'GROWING' | 'STABLE' | 'DECLINING' | 'IMPROVING' | 'EFFICIENT' | 'MODERATE' | 'OVERWHELMED' }>`
  padding: 0.25rem 0.75rem;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  
  ${props => {
    switch (props.trend) {
      case 'ACCELERATING':
      case 'IMPROVING':
      case 'EFFICIENT':
        return `
          background: #D1FAE5;
          color: #059669;
        `;
      case 'GROWING':
      case 'STABLE':
      case 'MODERATE':
        return `
          background: #FEF3C7;
          color: #D97706;
        `;
      case 'DECLINING':
      case 'OVERWHELMED':
        return `
          background: #FEE2E2;
          color: #DC2626;
        `;
      default:
        return `
          background: #F3F4F6;
          color: #6B7280;
        `;
    }
  }}
`;

const Reports: React.FC = () => {
  const [dateRange, setDateRange] = useState('30d');
  const [reportType, setReportType] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  const [userActivityData, setUserActivityData] = useState<any>(null);
  const [contentStats, setContentStats] = useState<any>(null);
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [userAnalytics, setUserAnalytics] = useState<any>(null);
  const [contentAnalytics, setContentAnalytics] = useState<any>(null);
  const [moderationAnalytics, setModerationAnalytics] = useState<any>(null);
  const [systemPerformance, setSystemPerformance] = useState<any>(null);
  const [realTimeAlerts, setRealTimeAlerts] = useState<any>(null);
  const [liveMetrics, setLiveMetrics] = useState<any>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [predictiveAnalytics, setPredictiveAnalytics] = useState<any>(null);
  const [customReport, setCustomReport] = useState<any>(null);
  const [reportConfig, setReportConfig] = useState({
    reportType: 'comprehensive',
    dateRange: '30d',
    metrics: ['users', 'content', 'moderation'],
    format: 'JSON'
  });

  useEffect(() => {
    loadReportsData();
  }, [dateRange, reportType]);

  // Auto-refresh functionality for real-time data
  useEffect(() => {
    if (!autoRefresh) return;

    const refreshInterval = setInterval(async () => {
      try {
        const [alertsRes, metricsRes] = await Promise.all([
          dashboardService.getRealTimeAlerts(),
          dashboardService.getLiveMetrics()
        ]);
        setRealTimeAlerts(alertsRes);
        setLiveMetrics(metricsRes);
        setLastRefresh(new Date());
      } catch (error) {
        console.error('Failed to refresh real-time data:', error);
      }
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(refreshInterval);
  }, [autoRefresh]);

  // Generate custom report
  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      const report = await dashboardService.generateCustomReport(reportConfig);
      setCustomReport(report);
      toast.success('Custom report generated successfully!');
    } catch (error) {
      console.error('Failed to generate custom report:', error);
      toast.error('Failed to generate custom report');
    } finally {
      setLoading(false);
    }
  };

  // Update report configuration
  const updateReportConfig = (key: string, value: any) => {
    setReportConfig(prev => ({ ...prev, [key]: value }));
  };

  // Toggle metric in report config
  const toggleMetric = (metric: string) => {
    setReportConfig(prev => ({
      ...prev,
      metrics: prev.metrics.includes(metric)
        ? prev.metrics.filter(m => m !== metric)
        : [...prev.metrics, metric]
    }));
  };

  const loadReportsData = async () => {
    try {
      setLoading(true);
      
      const [dashboardStatsRes, userActivityRes, contentStatsRes, queueStatsRes, userAnalyticsRes, contentAnalyticsRes, moderationRes, performanceRes, alertsRes, metricsRes, predictiveRes] = await Promise.all([
        dashboardService.getDashboardStats(),
        dashboardService.getUserActivityData(),
        dashboardService.getContentStats(),
        adminStoryService.getQueueStats(),
        dashboardService.getUserAnalytics(),
        dashboardService.getContentAnalytics(),
        dashboardService.getModerationAnalytics(),
        dashboardService.getSystemPerformance(),
        dashboardService.getRealTimeAlerts(),
        dashboardService.getLiveMetrics(),
        dashboardService.getPredictiveAnalytics()
      ]);
      
      setDashboardStats(dashboardStatsRes);
      setUserActivityData(userActivityRes);
      setContentStats(contentStatsRes);
      setQueueStats(queueStatsRes);
      setUserAnalytics(userAnalyticsRes);
      setContentAnalytics(contentAnalyticsRes);
      setModerationAnalytics(moderationRes);
      setSystemPerformance(performanceRes);
      setRealTimeAlerts(alertsRes);
      setLiveMetrics(metricsRes);
      setPredictiveAnalytics(predictiveRes);
    } catch (error) {
      console.error('Failed to load reports data:', error);
      toast.error('Failed to load reports data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate trend for metrics (simplified - in real app you'd compare with previous period)
  const getTrend = (current: number, previous?: number): 'up' | 'down' | 'neutral' => {
    if (!previous || previous === 0) return 'neutral';
    if (current > previous) return 'up';
    if (current < previous) return 'down';
    return 'neutral';
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up': return '📈';
      case 'down': return '📉';
      case 'neutral': return '➡️';
    }
  };

  const formatTrendText = (trend: 'up' | 'down' | 'neutral', value?: number) => {
    if (!value) return 'No change';
    switch (trend) {
      case 'up': return `+${value}% this week`;
      case 'down': return `-${value}% this week`;
      case 'neutral': return 'Stable';
    }
  };

  // Real data for charts based on backend responses
  const userGrowthData = userActivityData ? {
    labels: userActivityData.dailyActivity?.map((day: any) => {
      const date = new Date(day.date);
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
    }) || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'New Users',
        data: userActivityData.dailyActivity?.map((day: any) => day.newUsers) || [1, 0, 0, 1, 0, 0, 0],
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
      },
      {
        label: 'Active Users',
        data: userActivityData.dailyActivity?.map((day: any) => day.activeUsers) || [2, 1, 1, 2, 1, 0, 0],
        borderColor: '#10B981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
      },
    ],
  } : {
    labels: ['Loading...'],
    datasets: [{
      label: 'Loading',
      data: [0],
      borderColor: '#9CA3AF',
      backgroundColor: 'rgba(156, 163, 175, 0.1)',
    }],
  };

  const contentModerationData = contentStats ? {
    labels: ['Approved', 'Pending Review', 'Appeal Pending', 'Rejected'],
    datasets: [
      {
        data: [
          contentStats.storyStatusDistribution?.APPROVED || 0,
          contentStats.storyStatusDistribution?.PENDING_REVIEW || 0,
          contentStats.storyStatusDistribution?.APPEAL_PENDING || 0,
          contentStats.storyStatusDistribution?.REJECTED || 0,
        ],
        backgroundColor: ['#10B981', '#F59E0B', '#8B5CF6', '#EF4444'],
      },
    ],
  } : {
    labels: ['Loading...'],
    datasets: [{
      data: [1],
      backgroundColor: ['#9CA3AF'],
    }],
  };

  const aiPerformanceData = dashboardStats ? {
    labels: ['Stories Analyzed', 'Accuracy Rate', 'Auto-Approved', 'Flagged Content'],
    datasets: [
      {
        label: 'AI Performance Metrics',
        data: [
          dashboardStats.storiesWithAIAnalysis || 0,
          dashboardStats.totalStories > 0 ? Math.round((dashboardStats.storiesWithAIAnalysis / dashboardStats.totalStories) * 100) : 0,
          dashboardStats.approvedStories || 0,
          dashboardStats.inappropriateContentDetected || 0,
        ],
        backgroundColor: '#8B5CF6',
      },
    ],
  } : {
    labels: ['Loading...'],
    datasets: [{
      label: 'Loading',
      data: [0],
      backgroundColor: '#9CA3AF',
    }],
  };

  const engagementData = dashboardStats ? {
    labels: ['Published Stories', 'Active Users', 'Pending Reviews', 'Total Users'],
    datasets: [
      {
        data: [
          dashboardStats.approvedStories || 0,
          dashboardStats.activeUsers || 0,
          dashboardStats.pendingStories || 0,
          dashboardStats.totalUsers || 0,
        ],
        backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#EC4899'],
      },
    ],
  } : {
    labels: ['Loading...'],
    datasets: [{
      data: [1],
      backgroundColor: ['#9CA3AF'],
    }],
  };

  return (
    <ReportsContainer>
      <PageHeader>
        <h1>
          📊 Analytics & Reports
          {dashboardStats && (
            <span style={{ fontSize: '0.6rem', color: 'var(--color-gray-500)', fontWeight: 400 }}>
              Last updated: {new Date().toLocaleTimeString()}
            </span>
          )}
        </h1>
        <ExportButton>📄 Export Report</ExportButton>
      </PageHeader>

      <FilterBar>
        <Select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 3 months</option>
          <option value="1y">Last year</option>
        </Select>
        
        <Select value={reportType} onChange={(e) => setReportType(e.target.value)}>
          <option value="overview">Overview</option>
          <option value="users">User Analytics</option>
          <option value="content">Content Analytics</option>
          <option value="moderation">Moderation Report</option>
          <option value="ai">AI Performance</option>
        </Select>

        <DateInput type="date" />
        <DateInput type="date" />
      </FilterBar>

      {/* Overview Dashboard */}
      <SectionTitle>📊 Platform Overview</SectionTitle>
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '3rem',
          background: 'var(--color-white)',
          borderRadius: 'var(--border-radius-lg)',
          boxShadow: 'var(--shadow-md)',
          marginBottom: '2rem'
        }}>
          <LoadingSpinner message="Loading analytics data..." />
        </div>
      ) : (
        <MetricsGrid>
          <MetricCard>
            <MetricValue>
              {dashboardStats?.totalUsers || 0}
              <TrendIndicator trend="up">📈 New platform</TrendIndicator>
            </MetricValue>
            <MetricLabel>Total Registered Users</MetricLabel>
          </MetricCard>
          
          <MetricCard>
            <MetricValue>
              {dashboardStats?.activeUsers || 0}
              <TrendIndicator trend={getTrend(dashboardStats?.activeUsers || 0, dashboardStats?.totalUsers || 0)}>
                {getTrendIcon(getTrend(dashboardStats?.activeUsers || 0, dashboardStats?.totalUsers || 0))} Active now
              </TrendIndicator>
            </MetricValue>
            <MetricLabel>Currently Active Users</MetricLabel>
          </MetricCard>
          
          <MetricCard>
            <MetricValue>
              {dashboardStats?.totalStories || 0}
              <TrendIndicator trend="neutral">📚 Growing</TrendIndicator>
            </MetricValue>
            <MetricLabel>Total Stories Created</MetricLabel>
          </MetricCard>
          
          <MetricCard>
            <MetricValue>
              {dashboardStats?.approvedStories || 0}
              <TrendIndicator trend="up">✅ Quality content</TrendIndicator>
            </MetricValue>
            <MetricLabel>Published Stories</MetricLabel>
          </MetricCard>
          
          <MetricCard>
            <MetricValue>
              {queueStats?.totalPending || 0}
              <TrendIndicator trend={getTrend(queueStats?.totalPending || 0, 5)}>
                {queueStats?.totalPending === 0 ? '✅ All clear' : '⏳ In review'}
              </TrendIndicator>
            </MetricValue>
            <MetricLabel>Pending Moderation</MetricLabel>
          </MetricCard>
          
          <MetricCard>
            <MetricValue>
              {dashboardStats?.storiesWithAIAnalysis > 0 && dashboardStats?.totalStories > 0 
                ? `${Math.round((dashboardStats.storiesWithAIAnalysis / dashboardStats.totalStories) * 100)}%`
                : '0%'
              }
              <TrendIndicator trend="up">🤖 AI Protected</TrendIndicator>
            </MetricValue>
            <MetricLabel>AI Safety Coverage</MetricLabel>
          </MetricCard>
        </MetricsGrid>
      )}

      {/* Detailed Analytics Charts */}
      <SectionTitle>📈 Detailed Analytics</SectionTitle>
      {!loading && (
      <ReportsGrid>
        <ReportCard>
          <h3>📈 User Growth & Activity (Last 7 Days)</h3>
          <Line 
            data={userGrowthData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom' as const,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
          />
        </ReportCard>

        <ReportCard>
          <h3>🛡️ Content Safety & Moderation</h3>
          <Pie 
            data={contentModerationData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom' as const,
                },
              },
            }}
          />
        </ReportCard>

        <ReportCard>
          <h3>🤖 AI Content Analysis Performance</h3>
          <Bar 
            data={aiPerformanceData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  max: 100,
                },
              },
            }}
          />
        </ReportCard>

        <ReportCard>
          <h3>👥 Platform Engagement Overview</h3>
          <Pie 
            data={engagementData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom' as const,
                },
              },
            }}
          />
        </ReportCard>
      </ReportsGrid>
      )}

      {/* Detailed User Analytics Section */}
      {!loading && userAnalytics && (
        <>
          <SectionTitle>👥 Detailed User Analytics</SectionTitle>
          <MetricsGrid>
            <MetricCard>
              <MetricValue>
                {userAnalytics.engagement?.activeUserPercentage || 0}%
                <TrendIndicator trend="up">🔥 Engaged</TrendIndicator>
              </MetricValue>
              <MetricLabel>Active User Rate</MetricLabel>
            </MetricCard>
            
            <MetricCard>
              <MetricValue>
                {userAnalytics.engagement?.avgStoriesPerUser || 0}
                <TrendIndicator trend="neutral">📚 Per User</TrendIndicator>
              </MetricValue>
              <MetricLabel>Avg Stories Created</MetricLabel>
            </MetricCard>
            
            <MetricCard>
              <MetricValue>
                {userAnalytics.userGrowth?.newUsersLastWeek || 0}
                <TrendIndicator trend="up">🎆 This Week</TrendIndicator>
              </MetricValue>
              <MetricLabel>New User Registrations</MetricLabel>
            </MetricCard>
            
            <MetricCard>
              <MetricValue>
                {(userAnalytics.userStatusDistribution?.SUSPENDED || 0) + (userAnalytics.userStatusDistribution?.BANNED || 0)}
                <TrendIndicator trend={(userAnalytics.userStatusDistribution?.SUSPENDED || 0) + (userAnalytics.userStatusDistribution?.BANNED || 0) > 0 ? "down" : "up"}>
                  {(userAnalytics.userStatusDistribution?.SUSPENDED || 0) + (userAnalytics.userStatusDistribution?.BANNED || 0) === 0 ? '✅ All Good' : '⚠️ Needs Review'}
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>Suspended/Banned Users</MetricLabel>
            </MetricCard>
          </MetricsGrid>
          
          <ReportsGrid>
            <ReportCard>
              <h3>👥 User Status Distribution</h3>
              <Pie 
                data={{
                  labels: ['Active Users', 'Suspended', 'Banned'],
                  datasets: [{
                    data: [
                      userAnalytics.userStatusDistribution?.ACTIVE || 0,
                      userAnalytics.userStatusDistribution?.SUSPENDED || 0,
                      userAnalytics.userStatusDistribution?.BANNED || 0,
                    ],
                    backgroundColor: ['#10B981', '#F59E0B', '#EF4444'],
                  }],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'bottom' as const,
                    },
                  },
                }}
              />
            </ReportCard>

            <ReportCard>
              <h3>📈 User Registration Trend (6 Months)</h3>
              <Line 
                data={{
                  labels: userAnalytics.monthlyRegistrations?.map((month: any) => 
                    `${month.month.charAt(0) + month.month.slice(1).toLowerCase()} ${month.year}`
                  ) || ['Loading...'],
                  datasets: [{
                    label: 'New Registrations',
                    data: userAnalytics.monthlyRegistrations?.map((month: any) => month.count) || [0],
                    borderColor: '#3B82F6',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    tension: 0.4,
                  }],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'bottom' as const,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                    },
                  },
                }}
              />
            </ReportCard>
          </ReportsGrid>
        </>
      )}

      {/* Detailed Content Analytics Section */}
      {!loading && contentAnalytics && (
        <>
          <SectionTitle>📚 Detailed Content Analytics</SectionTitle>
          <MetricsGrid>
            <MetricCard>
              <MetricValue>
                {contentAnalytics.qualityMetrics?.aiAnalysisCoverage || 0}%
                <TrendIndicator trend="up">🤖 AI Scanned</TrendIndicator>
              </MetricValue>
              <MetricLabel>AI Safety Coverage</MetricLabel>
            </MetricCard>
            
            <MetricCard>
              <MetricValue>
                {contentAnalytics.qualityMetrics?.flaggedContentRate || 0}%
                <TrendIndicator trend={contentAnalytics.qualityMetrics?.flaggedContentRate > 10 ? "down" : "up"}>
                  {contentAnalytics.qualityMetrics?.flaggedContentRate < 5 ? '👍 Safe' : '⚠️ Monitor'}
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>Content Flag Rate</MetricLabel>
            </MetricCard>
            
            <MetricCard>
              <MetricValue>
                {contentAnalytics.storyMetrics?.publishedStories || 0}
                <TrendIndicator trend="up">🎆 Live</TrendIndicator>
              </MetricValue>
              <MetricLabel>Published Stories</MetricLabel>
            </MetricCard>
            
            <MetricCard>
              <MetricValue>
                {contentAnalytics.storyMetrics?.pendingStories || 0}
                <TrendIndicator trend={contentAnalytics.storyMetrics?.pendingStories > 5 ? "down" : "neutral"}>
                  {contentAnalytics.storyMetrics?.pendingStories === 0 ? '✅ Clear' : '⏳ Queue'}
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>Pending Moderation</MetricLabel>
            </MetricCard>
          </MetricsGrid>
          
          <ReportsGrid>
            <ReportCard>
              <h3>📈 Story Creation Trend (6 Months)</h3>
              <Bar 
                data={{
                  labels: contentAnalytics.monthlyCreations?.map((month: any) => 
                    `${month.month.charAt(0) + month.month.slice(1).toLowerCase()}`
                  ) || ['Loading...'],
                  datasets: [{
                    label: 'Stories Created',
                    data: contentAnalytics.monthlyCreations?.map((month: any) => month.count) || [0],
                    backgroundColor: '#8B5CF6',
                  }],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                    },
                  },
                }}
              />
            </ReportCard>

            <ReportCard>
              <h3>🎆 Popular Story Genres</h3>
              <Pie 
                data={{
                  labels: contentAnalytics.popularGenres?.map((genre: any) => genre.genre) || ['Loading...'],
                  datasets: [{
                    data: contentAnalytics.popularGenres?.map((genre: any) => genre.count) || [0],
                    backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'],
                  }],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'bottom' as const,
                    },
                  },
                }}
              />
            </ReportCard>
          </ReportsGrid>
        </>
      )}

      {/* Phase 3: Advanced Moderation & System Analytics */}
      <SectionTitle>🛡️ Advanced Moderation Analytics</SectionTitle>
      
      {moderationAnalytics && (
        <>
          <MetricsGrid>
            <MetricCard>
              <MetricValue>
                {moderationAnalytics.aiModeration?.totalContentReviewed || 0}
                <TrendIndicator trend={moderationAnalytics.aiModeration?.aiCoverageRate > 50 ? 'up' : 'neutral'}>
                  {moderationAnalytics.aiModeration?.aiCoverageRate?.toFixed(1)}% coverage
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>AI Reviews Completed</MetricLabel>
            </MetricCard>
            
            <MetricCard>
              <MetricValue>
                {moderationAnalytics.actionRequired?.totalItemsNeedingAction || 0}
                <TrendIndicator trend={(moderationAnalytics.actionRequired?.totalItemsNeedingAction || 0) > 5 ? 'down' : 'up'}>
                  {(moderationAnalytics.actionRequired?.totalItemsNeedingAction || 0) > 5 ? 'High' : 'Low'}
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>Items Needing Action</MetricLabel>
            </MetricCard>
            
            <MetricCard>
              <MetricValue>
                {moderationAnalytics.aiModeration?.flaggedContentRate?.toFixed(1) || 0}%
                <TrendIndicator trend={(moderationAnalytics.aiModeration?.flaggedContentRate || 0) < 10 ? 'up' : 'down'}>
                  {(moderationAnalytics.aiModeration?.flaggedContentRate || 0) < 10 ? 'Good' : 'Alert'}
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>Content Flagged Rate</MetricLabel>
            </MetricCard>
            
            <MetricCard>
              <MetricValue>
                {moderationAnalytics.aiModeration?.approvalRate?.toFixed(1) || 0}%
                <TrendIndicator trend={(moderationAnalytics.aiModeration?.approvalRate || 0) > 70 ? 'up' : 'neutral'}>
                  {(moderationAnalytics.aiModeration?.approvalRate || 0) > 70 ? 'High' : 'Normal'}
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>Content Approval Rate</MetricLabel>
            </MetricCard>
          </MetricsGrid>

          <ReportsGrid>
            <ReportCard>
              <h3>📊 Publication Status Distribution</h3>
              <Pie 
                data={{
                  labels: Object.keys(moderationAnalytics.publicationStatus || {}),
                  datasets: [{
                    data: Object.values(moderationAnalytics.publicationStatus || {}),
                    backgroundColor: [
                      '#10B981', // APPROVED - green
                      '#F59E0B', // PENDING_REVIEW - yellow
                      '#EF4444', // REJECTED - red
                      '#8B5CF6', // APPEAL_PENDING - purple
                      '#6B7280'  // DRAFT - gray
                    ],
                    borderWidth: 2,
                    borderColor: '#ffffff'
                  }]
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'bottom' as const,
                      labels: {
                        padding: 20,
                        usePointStyle: true
                      }
                    }
                  }
                }}
              />
            </ReportCard>

            <ReportCard>
              <h3>⏰ Moderation Queue Status</h3>
              <Bar 
                data={{
                  labels: ['AI Review', 'Admin Review', 'General Review', 'Appeals'],
                  datasets: [{
                    label: 'Items in Queue',
                    data: [
                      moderationAnalytics.queueStatus?.pendingAIReview || 0,
                      moderationAnalytics.queueStatus?.pendingAdminReview || 0,
                      moderationAnalytics.queueStatus?.pendingGeneralReview || 0,
                      moderationAnalytics.queueStatus?.appealsWaiting || 0
                    ],
                    backgroundColor: ['#3B82F6', '#F59E0B', '#10B981', '#8B5CF6'],
                    borderColor: ['#2563EB', '#D97706', '#059669', '#7C3AED'],
                    borderWidth: 1
                  }]
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { display: false }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: { precision: 0 }
                    }
                  }
                }}
              />
            </ReportCard>
          </ReportsGrid>
        </>
      )}

      <SectionTitle>⚡ System Performance Analytics</SectionTitle>
      
      {systemPerformance && (
        <>
          <MetricsGrid>
            <MetricCard>
              <MetricValue>
                {systemPerformance.database?.totalUsers || 0}
                <TrendIndicator trend='up'>
                  Active: {systemPerformance.database?.activeUsers || 0}
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>Total Database Users</MetricLabel>
            </MetricCard>
            
            <MetricCard>
              <MetricValue>
                {systemPerformance.contentHealth?.processingRate?.toFixed(1) || 0}%
                <TrendIndicator trend={systemPerformance.contentHealth?.processingRate > 75 ? 'up' : 'neutral'}>
                  {systemPerformance.contentHealth?.processingRate > 75 ? 'Optimal' : 'Normal'}
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>Content Processing Rate</MetricLabel>
            </MetricCard>
            
            <MetricCard>
              <MetricValue>
                {systemPerformance.systemLoad?.overallStatus || 'UNKNOWN'}
                <TrendIndicator trend={systemPerformance.systemLoad?.overallStatus === 'HEALTHY' ? 'up' : 'neutral'}>
                  {systemPerformance.systemLoad?.userLoadPercentage?.toFixed(1) || 0}% load
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>System Status</MetricLabel>
            </MetricCard>
            
            <MetricCard>
              <MetricValue>
                {systemPerformance.resources?.databaseRecords || 0}
                <TrendIndicator trend='neutral'>
                  {systemPerformance.resources?.contentUnderReview || 0} pending
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>Total Database Records</MetricLabel>
            </MetricCard>
          </MetricsGrid>

          <ReportsGrid>
            <ReportCard>
              <h3>📈 System Load Overview</h3>
              <Bar 
                data={{
                  labels: ['User Load', 'Content Load', 'Processing Queue'],
                  datasets: [{
                    label: 'Load Percentage',
                    data: [
                      systemPerformance.systemLoad?.userLoadPercentage || 0,
                      systemPerformance.systemLoad?.contentLoadPercentage || 0,
                      systemPerformance.contentHealth?.backlogPercentage || 0
                    ],
                    backgroundColor: [
                      (systemPerformance.systemLoad?.userLoadPercentage || 0) > 80 ? '#EF4444' : '#10B981',
                      (systemPerformance.systemLoad?.contentLoadPercentage || 0) > 50 ? '#F59E0B' : '#10B981',
                      (systemPerformance.contentHealth?.backlogPercentage || 0) > 25 ? '#EF4444' : '#10B981'
                    ],
                    borderWidth: 1
                  }]
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { display: false }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 100,
                      ticks: {
                        callback: (value: any) => value + '%'
                      }
                    }
                  }
                }}
              />
            </ReportCard>

            <ReportCard>
              <h3>💡 System Recommendations</h3>
              <div style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                {systemPerformance.recommendations?.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                    {systemPerformance.recommendations.map((rec: string, index: number) => (
                      <li key={index} style={{ marginBottom: '0.5rem' }}>{rec}</li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: 0, color: '#6B7280' }}>No recommendations at this time.</p>
                )}
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.5rem', 
                  background: '#F3F4F6', 
                  borderRadius: '6px', 
                  fontSize: '0.8rem',
                  color: '#6B7280'
                }}>
                  Last updated: {systemPerformance.lastUpdated ? new Date(systemPerformance.lastUpdated).toLocaleString() : 'Unknown'}
                </div>
              </div>
            </ReportCard>
          </ReportsGrid>
        </>
      )}

      {/* Phase 4: Real-time Monitoring & Alerting */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <SectionTitle>🔴 Real-time Monitoring & Alerts</SectionTitle>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <AutoRefreshToggle active={autoRefresh} onClick={() => setAutoRefresh(!autoRefresh)}>
            {autoRefresh ? '⏸️' : '▶️'} Auto Refresh
          </AutoRefreshToggle>
          <RefreshIndicator active={autoRefresh}>
            <div className="refresh-dot" />
            Last updated: {lastRefresh.toLocaleTimeString()}
          </RefreshIndicator>
        </div>
      </div>

      {realTimeAlerts && (
        <>
          {/* Critical Alerts Section */}
          <ReportCard>
            <h3>🚨 Critical Alerts ({realTimeAlerts.criticalAlerts?.length || 0})</h3>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {realTimeAlerts.criticalAlerts?.length > 0 ? (
                realTimeAlerts.criticalAlerts.map((alert: any, index: number) => (
                  <AlertBadge key={alert.id || index} severity={alert.severity}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {alert.title}
                      </div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                        {alert.message}
                      </div>
                      <div style={{ fontSize: '0.7rem', marginTop: '0.25rem', opacity: 0.6 }}>
                        {alert.category} • {new Date(alert.timestamp).toLocaleString()}
                      </div>
                    </div>
                    {alert.actionRequired && (
                      <div style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                        ACTION REQUIRED
                      </div>
                    )}
                  </AlertBadge>
                ))
              ) : (
                <div style={{ textAlign: 'center', color: '#6B7280', padding: '2rem' }}>
                  ✅ No active alerts - All systems operating normally
                </div>
              )}
            </div>
          </ReportCard>

          {/* System Health Status */}
          <MetricsGrid>
            <MetricCard>
              <MetricValue>
                {realTimeAlerts.systemHealth?.status || 'UNKNOWN'}
                <TrendIndicator trend={realTimeAlerts.systemHealth?.status === 'HEALTHY' ? 'up' : 'down'}>
                  {realTimeAlerts.systemHealth?.uptime || 'N/A'}
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>System Health Status</MetricLabel>
            </MetricCard>

            <MetricCard>
              <MetricValue>
                {Math.round(realTimeAlerts.systemHealth?.responseTime || 0)}ms
                <TrendIndicator trend={(realTimeAlerts.systemHealth?.responseTime || 0) < 200 ? 'up' : 'neutral'}>
                  {(realTimeAlerts.systemHealth?.responseTime || 0) < 200 ? 'Fast' : 'Normal'}
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>Average Response Time</MetricLabel>
            </MetricCard>

            <MetricCard>
              <MetricValue>
                {realTimeAlerts.performanceMetrics?.databaseConnections || 0}
                <TrendIndicator trend='neutral'>
                  Active
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>Database Connections</MetricLabel>
            </MetricCard>

            <MetricCard>
              <MetricValue>
                {realTimeAlerts.performanceMetrics?.activeRequests || 0}
                <TrendIndicator trend={realTimeAlerts.performanceMetrics?.activeRequests < 50 ? 'up' : 'neutral'}>
                  Live
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>Active Requests</MetricLabel>
            </MetricCard>
          </MetricsGrid>
        </>
      )}

      {liveMetrics && (
        <>
          <SectionTitle>📊 Live System Metrics</SectionTitle>
          
          <ReportsGrid>
            {/* Live User Activity */}
            <ReportCard>
              <h3>👥 Live User Activity</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <LiveMetricCard>
                  <div className="metric-header">
                    <div className="metric-title">Active Users</div>
                  </div>
                  <div className="metric-value">
                    {liveMetrics.userActivity?.activeUsers || 0}
                    <span className="metric-unit">online</span>
                  </div>
                </LiveMetricCard>
                <LiveMetricCard>
                  <div className="metric-header">
                    <div className="metric-title">New Today</div>
                  </div>
                  <div className="metric-value">
                    {liveMetrics.userActivity?.newUsersToday || 0}
                    <span className="metric-unit">users</span>
                  </div>
                </LiveMetricCard>
              </div>
            </ReportCard>

            {/* Live Content Activity */}
            <ReportCard>
              <h3>📚 Live Content Activity</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <LiveMetricCard>
                  <div className="metric-header">
                    <div className="metric-title">Published Stories</div>
                  </div>
                  <div className="metric-value">
                    {liveMetrics.contentActivity?.publishedStories || 0}
                    <span className="metric-unit">live</span>
                  </div>
                </LiveMetricCard>
                <LiveMetricCard>
                  <div className="metric-header">
                    <div className="metric-title">Pending Review</div>
                  </div>
                  <div className="metric-value">
                    {liveMetrics.contentActivity?.pendingReview || 0}
                    <span className="metric-unit">items</span>
                  </div>
                </LiveMetricCard>
              </div>
            </ReportCard>

            {/* System Resources */}
            <ReportCard>
              <h3>💻 System Resources</h3>
              <Bar 
                data={{
                  labels: ['Memory', 'CPU', 'Disk', 'Network'],
                  datasets: [{
                    label: 'Usage %',
                    data: [
                      liveMetrics.systemResources?.memoryUsage || 0,
                      liveMetrics.systemResources?.cpuUsage || 0,
                      liveMetrics.systemResources?.diskUsage || 0,
                      liveMetrics.systemResources?.networkTraffic || 0
                    ],
                    backgroundColor: [
                      (liveMetrics.systemResources?.memoryUsage || 0) > 80 ? '#EF4444' : '#10B981',
                      (liveMetrics.systemResources?.cpuUsage || 0) > 80 ? '#EF4444' : '#3B82F6',
                      (liveMetrics.systemResources?.diskUsage || 0) > 85 ? '#EF4444' : '#8B5CF6',
                      (liveMetrics.systemResources?.networkTraffic || 0) > 90 ? '#EF4444' : '#F59E0B'
                    ],
                    borderWidth: 1
                  }]
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { display: false }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 100,
                      ticks: {
                        callback: (value: any) => value + '%'
                      }
                    }
                  }
                }}
              />
            </ReportCard>

            {/* Response Time Metrics */}
            <ReportCard>
              <h3>⚡ Response Time Metrics</h3>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', color: '#6B7280' }}>
                  <span>Average: {liveMetrics.responseMetrics?.averageResponseTime || 0}ms</span>
                  <span>Requests/min: {liveMetrics.responseMetrics?.requestsPerMinute || 0}</span>
                </div>
              </div>
              <Bar 
                data={{
                  labels: ['Min', 'Avg', 'Max'],
                  datasets: [{
                    label: 'Response Time (ms)',
                    data: [
                      liveMetrics.responseMetrics?.minResponseTime || 0,
                      liveMetrics.responseMetrics?.averageResponseTime || 0,
                      liveMetrics.responseMetrics?.maxResponseTime || 0
                    ],
                    backgroundColor: ['#10B981', '#3B82F6', '#F59E0B'],
                    borderWidth: 1
                  }]
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { display: false }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: (value: any) => value + 'ms'
                      }
                    }
                  }
                }}
              />
            </ReportCard>
          </ReportsGrid>

          {/* Recent Activity Feed */}
          <ReportCard>
            <h3>📈 Recent Activity Feed</h3>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {realTimeAlerts?.recentActivity?.length > 0 ? (
                realTimeAlerts.recentActivity.map((activity: any, index: number) => (
                  <div key={activity.id || index} style={{ 
                    padding: '0.75rem', 
                    borderBottom: '1px solid #E5E7EB', 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '0.9rem' }}>
                        {activity.message}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                        {activity.type} • {new Date(activity.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      background: activity.severity === 'INFO' ? '#F0F9FF' : '#FEF3C7',
                      color: activity.severity === 'INFO' ? '#0369A1' : '#D97706'
                    }}>
                      {activity.severity}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', color: '#6B7280', padding: '2rem' }}>
                  No recent activity recorded
                </div>
              )}
            </div>
          </ReportCard>
        </>
      )}

      {/* Phase 5: Predictive Analytics & Custom Reporting */}
      <SectionTitle>🔮 Predictive Analytics & Forecasting</SectionTitle>

      {predictiveAnalytics && (
        <>
          {/* Trend Analysis Summary */}
          <MetricsGrid>
            <MetricCard>
              <MetricValue>
                <TrendBadge trend={predictiveAnalytics.trendAnalysis?.userGrowthTrend}>
                  {predictiveAnalytics.trendAnalysis?.userGrowthTrend}
                </TrendBadge>
              </MetricValue>
              <MetricLabel>User Growth Trend</MetricLabel>
            </MetricCard>

            <MetricCard>
              <MetricValue>
                <TrendBadge trend={predictiveAnalytics.trendAnalysis?.contentQualityTrend}>
                  {predictiveAnalytics.trendAnalysis?.contentQualityTrend}
                </TrendBadge>
              </MetricValue>
              <MetricLabel>Content Quality Trend</MetricLabel>
            </MetricCard>

            <MetricCard>
              <MetricValue>
                <TrendBadge trend={predictiveAnalytics.trendAnalysis?.moderationEfficiency}>
                  {predictiveAnalytics.trendAnalysis?.moderationEfficiency}
                </TrendBadge>
              </MetricValue>
              <MetricLabel>Moderation Efficiency</MetricLabel>
            </MetricCard>

            <MetricCard>
              <MetricValue>
                {predictiveAnalytics.userGrowthForecast?.monthlyGrowthRate?.toFixed(1) || 0}%
                <TrendIndicator trend='up'>
                  Monthly Growth
                </TrendIndicator>
              </MetricValue>
              <MetricLabel>Current Growth Rate</MetricLabel>
            </MetricCard>
          </MetricsGrid>

          <ReportsGrid>
            {/* User Growth Forecast */}
            <ReportCard>
              <h3>📈 User Growth Forecast (6 Months)</h3>
              <ForecastChart>
                {predictiveAnalytics.userGrowthForecast?.forecast?.slice(0, 4).map((forecast: any, index: number) => (
                  <div key={index} className="forecast-item">
                    <div className="forecast-period">
                      {forecast.month.charAt(0) + forecast.month.slice(1).toLowerCase()} {forecast.year}
                    </div>
                    <div className="forecast-value">
                      {forecast.predictedUsers} users
                    </div>
                    <div className="confidence-badge">
                      {forecast.confidence}% confidence
                    </div>
                  </div>
                ))}
              </ForecastChart>
            </ReportCard>

            {/* Content Production Forecast */}
            <ReportCard>
              <h3>📚 Content Production Forecast</h3>
              <ForecastChart>
                {predictiveAnalytics.contentForecast?.forecast?.slice(0, 4).map((forecast: any, index: number) => (
                  <div key={index} className="forecast-item">
                    <div className="forecast-period">
                      {forecast.month.charAt(0) + forecast.month.slice(1).toLowerCase()} {forecast.year}
                    </div>
                    <div className="forecast-value">
                      {forecast.predictedStories} stories ({forecast.predictedApproved} approved)
                    </div>
                    <div className="confidence-badge">
                      {forecast.confidence}% confidence
                    </div>
                  </div>
                ))}
              </ForecastChart>
            </ReportCard>

            {/* Moderation Workload Prediction */}
            <ReportCard>
              <h3>🛡️ Moderation Workload Prediction</h3>
              <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#6B7280' }}>
                Current flagging rate: {predictiveAnalytics.moderationPrediction?.currentFlaggingRate}%
              </div>
              <Bar 
                data={{
                  labels: predictiveAnalytics.moderationPrediction?.forecast?.slice(0, 4).map((f: any) => 
                    f.month.charAt(0) + f.month.slice(1).toLowerCase()
                  ) || [],
                  datasets: [
                    {
                      label: 'Predicted Reviews',
                      data: predictiveAnalytics.moderationPrediction?.forecast?.slice(0, 4).map((f: any) => f.predictedReviews) || [],
                      backgroundColor: '#F59E0B',
                    },
                    {
                      label: 'Estimated Hours',
                      data: predictiveAnalytics.moderationPrediction?.forecast?.slice(0, 4).map((f: any) => f.estimatedHours) || [],
                      backgroundColor: '#8B5CF6',
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'bottom' as const,
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: { precision: 0 }
                    }
                  }
                }}
              />
            </ReportCard>

            {/* Strategic Recommendations */}
            <ReportCard>
              <h3>💡 Strategic Recommendations</h3>
              <div style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                {predictiveAnalytics.strategicRecommendations?.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                    {predictiveAnalytics.strategicRecommendations.map((rec: string, index: number) => (
                      <li key={index} style={{ marginBottom: '0.75rem', color: '#374151' }}>
                        {rec}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: 0, color: '#6B7280' }}>No strategic recommendations at this time.</p>
                )}
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem', 
                  background: '#F0F9FF', 
                  borderRadius: '6px', 
                  fontSize: '0.8rem',
                  color: '#0369A1',
                  borderLeft: '3px solid #0EA5E9'
                }}>
                  <strong>Forecast Horizon:</strong> {predictiveAnalytics.forecastHorizon} | 
                  <strong> Generated:</strong> {predictiveAnalytics.generatedAt ? new Date(predictiveAnalytics.generatedAt).toLocaleString() : 'Unknown'}
                </div>
              </div>
            </ReportCard>
          </ReportsGrid>
        </>
      )}

      {/* Custom Report Builder */}
      <SectionTitle>📊 Custom Report Builder</SectionTitle>
      
      <ReportsGrid>
        <ReportBuilder>
          <h3 style={{ marginTop: 0 }}>🛠️ Configure Custom Report</h3>
          
          <div className="config-section">
            <label>Report Type</label>
            <select 
              value={reportConfig.reportType} 
              onChange={(e) => updateReportConfig('reportType', e.target.value)}
            >
              <option value="comprehensive">Comprehensive Analysis</option>
              <option value="summary">Executive Summary</option>
              <option value="performance">Performance Report</option>
              <option value="compliance">Compliance Report</option>
            </select>
          </div>

          <div className="config-section">
            <label>Date Range</label>
            <select 
              value={reportConfig.dateRange} 
              onChange={(e) => updateReportConfig('dateRange', e.target.value)}
            >
              <option value="1d">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="1y">Last Year</option>
            </select>
          </div>

          <div className="config-section">
            <label>Include Metrics</label>
            <div className="metrics-checkboxes">
              {[
                { key: 'users', label: '👥 User Analytics' },
                { key: 'content', label: '📚 Content Analytics' },
                { key: 'moderation', label: '🛡️ Moderation Data' },
                { key: 'performance', label: '⚡ System Performance' }
              ].map((metric) => (
                <label key={metric.key}>
                  <input
                    type="checkbox"
                    checked={reportConfig.metrics.includes(metric.key)}
                    onChange={() => toggleMetric(metric.key)}
                  />
                  {metric.label}
                </label>
              ))}
            </div>
          </div>

          <div className="config-section">
            <label>Export Format</label>
            <select 
              value={reportConfig.format} 
              onChange={(e) => updateReportConfig('format', e.target.value)}
            >
              <option value="JSON">JSON Data</option>
              <option value="CSV">CSV Spreadsheet</option>
              <option value="PDF">PDF Document</option>
            </select>
          </div>

          <GenerateButton 
            onClick={handleGenerateReport}
            disabled={loading || reportConfig.metrics.length === 0}
          >
            {loading ? '⏳ Generating Report...' : '🚀 Generate Report'}
          </GenerateButton>
        </ReportBuilder>

        {/* Generated Report Display */}
        {customReport && (
          <ReportCard>
            <h3>📋 Generated Report: {customReport.reportType}</h3>
            
            <div style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>
              <div><strong>Report ID:</strong> {customReport.reportId}</div>
              <div><strong>Generated:</strong> {new Date(customReport.generatedAt).toLocaleString()}</div>
              <div><strong>Date Range:</strong> {customReport.dateRange}</div>
              <div><strong>Format:</strong> {customReport.format}</div>
            </div>

            {/* Report Summary */}
            {customReport.summary && (
              <div style={{ padding: '1rem', background: '#F9FAFB', borderRadius: '8px', marginBottom: '1rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0' }}>📊 Summary</h4>
                {Object.entries(customReport.summary).map(([key, value]) => (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span style={{ textTransform: 'capitalize' }}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}:
                    </span>
                    <strong>{typeof value === 'number' ? value.toFixed(1) + '%' : String(value)}</strong>
                  </div>
                ))}
              </div>
            )}

            {/* Report Data Preview */}
            {customReport.data && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0' }}>📈 Data Preview</h4>
                <div style={{ maxHeight: '200px', overflow: 'auto', fontSize: '0.8rem' }}>
                  <pre style={{ 
                    background: '#F3F4F6', 
                    padding: '1rem', 
                    borderRadius: '6px', 
                    margin: 0,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {JSON.stringify(customReport.data, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Export Options */}
            {customReport.exportInfo && (
              <div style={{ 
                padding: '1rem', 
                background: '#EFF6FF', 
                borderRadius: '8px', 
                border: '1px solid #DBEAFE' 
              }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#1D4ED8' }}>💾 Export Options</h4>
                <div style={{ fontSize: '0.875rem', color: '#1E40AF', marginBottom: '0.5rem' }}>
                  Available formats: {customReport.exportInfo.availableFormats.join(', ')}
                </div>
                <ExportButton 
                  onClick={() => window.open(customReport.exportInfo.downloadUrl, '_blank')}
                  style={{ width: 'auto', marginTop: '0.5rem' }}
                >
                  📥 Download Report
                </ExportButton>
                <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.5rem' }}>
                  Expires: {new Date(customReport.exportInfo.expiresAt).toLocaleString()}
                </div>
              </div>
            )}
          </ReportCard>
        )}
      </ReportsGrid>
    </ReportsContainer>
  );
};

export default Reports;